import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useCaseList from "../../hooks/useCaseList";
import useErrorHandler from "../../hooks/useErrorHandler";
import useSnackbar from "../../hooks/useSnackbar";
import {
  addCaseDeadline,
  completeDeadline,
  extendDeadline,
  getDeadlineAudit,
  getDeadlineRules,
} from "../../api/deadlines";

export const fmtDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(
        new Date(`${String(iso).slice(0, 10)}T00:00:00`)
      )
    : "";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const dayTypeNote = (rule) =>
  `${rule.duration_days} ${rule.day_type === "BUSINESS" ? "business" : "calendar"} days from trigger`;

export function CompleteDialog({ deadline, onClose, onDone }) {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setNote(""), [deadline?.id]);

  const submit = () => {
    setBusy(true);
    completeDeadline(user.token, deadline.id, note.trim() ? { note: note.trim() } : {})
      .then(() => {
        snackbar.showSuccess("Deadline marked as complete");
        onDone();
      })
      .catch(errorHandler)
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={!!deadline} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Mark deadline complete?</DialogTitle>
      <DialogContent>
        <Typography className="text-[13px] text-[#344767] mb-3">
          {deadline?.name} for {deadline?.case_title}, due {fmtDate(deadline?.due_date)}.
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Complete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ExtendDialog({ deadline, onClose, onDone }) {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [dueDate, setDueDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDueDate("");
    setReason("");
  }, [deadline?.id]);

  const current = deadline ? String(deadline.due_date).slice(0, 10) : "";
  const invalid = !dueDate || dueDate <= current || !reason.trim();

  const submit = () => {
    setBusy(true);
    extendDeadline(user.token, deadline.id, { due_date: dueDate, reason: reason.trim() })
      .then(() => {
        snackbar.showSuccess("Deadline extended");
        onDone();
      })
      .catch(errorHandler)
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={!!deadline} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Extend deadline</DialogTitle>
      <DialogContent className="flex flex-col gap-4">
        <Typography className="text-[13px] text-[#344767]">
          {deadline?.name} for {deadline?.case_title} is currently due {fmtDate(current)}.
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="New due date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: current }}
          error={!!dueDate && dueDate <= current}
          helperText={dueDate && dueDate <= current ? "Must be later than the current due date" : " "}
          className="mt-2"
        />
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          required
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || invalid}>
          {busy ? <CircularProgress size={18} /> : "Extend"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const auditLabels = {
  CREATED: "Created",
  EXTENDED: "Extended",
  COMPLETED: "Completed",
  REMINDER: "Reminder sent",
  ESCALATION: "Escalated",
};

export function AuditDialog({ deadline, onClose }) {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!deadline) return undefined;
    let cancelled = false;
    setLoading(true);
    setRows([]);
    getDeadlineAudit(user.token, deadline.id)
      .then(({ data }) => !cancelled && setRows(data.data))
      .catch((err) => !cancelled && errorHandler(err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [deadline?.id]);

  return (
    <Dialog open={!!deadline} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Audit trail</DialogTitle>
      <DialogContent>
        <Typography className="text-[13px] text-[#6b7a90] mb-3">
          {deadline?.name} · {deadline?.case_title}
        </Typography>
        {loading ? (
          <div className="py-8 grid place-items-center">
            <CircularProgress size={26} />
          </div>
        ) : !rows.length ? (
          <Typography className="py-6 text-center text-[#6b7a90]">No activity recorded.</Typography>
        ) : (
          <ul className="m-0 p-0 list-none">
            {rows.map((r, i) => (
              <li key={i} className="py-2 border-b border-[#eef3fb] last:border-0">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-semibold text-[13px] text-[#0d1b36]">{auditLabels[r.action] || r.action}</span>
                  <span className="text-[12px] text-[#6b7a90]">
                    {new Date(r.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <div className="text-[12px] text-[#6b7a90]">{r.user_name || "System"}</div>
                {r.note ? <div className="text-[13px] text-[#344767] mt-0.5 break-words">{r.note}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AddDeadlineDialog({ open, defaultCaseId, onClose, onDone }) {
  const { data: user } = useSelector((s) => s.user);
  const { caseList } = useCaseList();
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [rules, setRules] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [ruleId, setRuleId] = useState("");
  const [trigger, setTrigger] = useState(todayIso());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCaseId(defaultCaseId ? String(defaultCaseId) : "");
    setRuleId("");
    setTrigger(todayIso());
    getDeadlineRules(user.token)
      .then(({ data }) => setRules(data.data.filter((r) => r.active)))
      .catch(errorHandler);
  }, [open]);

  const rule = rules.find((r) => String(r.id) === ruleId);

  const submit = () => {
    setBusy(true);
    addCaseDeadline(user.token, caseId, { rule_id: Number(ruleId), trigger_date: trigger })
      .then(() => {
        snackbar.showSuccess("Deadline added");
        onDone();
      })
      .catch(errorHandler)
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add deadline</DialogTitle>
      <DialogContent className="flex flex-col gap-4">
        <FormControl size="small" fullWidth className="mt-2">
          <InputLabel id="dl-case">Case</InputLabel>
          <Select labelId="dl-case" label="Case" value={caseId} onChange={(e) => setCaseId(e.target.value)}>
            {caseList.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="dl-rule">Rule</InputLabel>
          <Select labelId="dl-rule" label="Rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
            {rules.map((r) => (
              <MenuItem key={r.id} value={String(r.id)}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Trigger date"
          value={trigger}
          onChange={(e) => setTrigger(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {rule ? (
          <Typography className="text-[13px] text-[#344767] bg-[#eef3fb] rounded-lg px-3 py-2">
            {dayTypeNote(rule)}
            {rule.stage ? ` · stage: ${rule.stage}` : ""}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || !caseId || !ruleId || !trigger}>
          {busy ? <CircularProgress size={18} /> : "Add deadline"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
